import * as React from "react";
import { Card } from "../card";
import { Icon } from "../icon";
import { cn } from "../utils";

/**
 * DismissibleCard — a <Card> with a hover-revealed dismiss affordance and a
 * very visual exit animation. Built for the app shell's "Now" rail, where both
 * the next-best-action suggestions and the activity entries render as their own
 * cards and dismissing one should feel tactile, not instant.
 *
 * The dismiss runs a pure-CSS keyframe (`--animate-card-dismiss`, defined in
 * the shell's globals.css @theme): the card slides right + fades while the
 * wrapper collapses its max-height + margin to 0, so the cards below smoothly
 * fill the gap. framer-motion is intentionally NOT a dependency.
 *
 * On animation end we call `onDismiss()` — the parent then drops the item from
 * its list (and persists the dismissal). Keeping the card mounted only for the
 * duration of the animation means the visual is owned here, not in every caller.
 *
 *   <DismissibleCard onDismiss={() => hide(id)} interactive asChild
 *                    dismissLabel={`Dismiss ${title}`}>
 *     <Link href={href}>…</Link>
 *   </DismissibleCard>
 */
export function DismissibleCard({
  onDismiss,
  dismissLabel = "Dismiss",
  children,
  className,
  interactive = false,
  asChild = false,
  persistentDismiss = false,
}: {
  /** Called once the exit animation completes; parent removes + persists. */
  onDismiss: () => void;
  /** Accessible label for the X button. */
  dismissLabel?: string;
  /**
   * The card body. Pass a node for the default treatment (a hover-revealed
   * dismiss X in the top-right corner). Pass a render-function to place the
   * trigger yourself — it receives `dismiss()` (runs the same slide-out) and
   * `leaving`; the built-in corner X is then omitted. The activity rail uses
   * this so its expand caret keeps the far-right edge and the dismiss shares
   * the clock's slot instead.
   */
  children:
    | React.ReactNode
    | ((api: { dismiss: () => void; leaving: boolean }) => React.ReactNode);
  className?: string;
  /** Forwarded to <Card> — clickable affordance when the card links somewhere. */
  interactive?: boolean;
  /** Forwarded to <Card> — render onto the child (e.g. a <Link>). */
  asChild?: boolean;
  /**
   * Keep the dismiss X visible at rest (muted) instead of revealing it only on
   * hover. Use where the affordance must be obviously discoverable rather than
   * tucked away — e.g. the shell's activity rail.
   */
  persistentDismiss?: boolean;
}) {
  const [leaving, setLeaving] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  function startDismiss() {
    // Pin the current rendered height as the keyframe's starting max-height so
    // the collapse animates from the real size rather than a guessed value.
    const el = wrapperRef.current;
    if (el) el.style.setProperty("--card-dismiss-h", `${el.scrollHeight}px`);
    setLeaving(true);
  }

  return (
    <div
      ref={wrapperRef}
      data-leaving={leaving ? "" : undefined}
      onAnimationEnd={leaving ? onDismiss : undefined}
      className={cn(
        "relative mb-2 overflow-hidden last:mb-0",
        leaving && "animate-card-dismiss pointer-events-none",
      )}
      style={{ "--card-dismiss-mb": "0.5rem" } as React.CSSProperties}
    >
      <Card
        interactive={interactive}
        asChild={asChild}
        className={cn("group/dismiss relative", className)}
      >
        {typeof children === "function"
          ? children({ dismiss: startDismiss, leaving })
          : children}
      </Card>
      {/* Default affordance: a dismiss X in the top-right corner, revealed on
          card hover (always reachable by kbd) and rendered OUTSIDE the Card's
          asChild <Link> so a click never navigates. Callers that pass a
          render-child own the trigger's placement themselves, so we skip it. */}
      {typeof children === "function" ? null : (
        <button
          type="button"
          aria-label={dismissLabel}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startDismiss();
          }}
          disabled={leaving}
          className={cn(
            "absolute right-2 top-2 z-10 rounded p-0.5 text-muted-foreground/50",
            "transition-all group-hover/dismiss:opacity-100",
            persistentDismiss ? "opacity-60" : "opacity-0",
            "hover:bg-secondary hover:text-foreground",
            "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          )}
        >
          <Icon name="x" size="sm" />
        </button>
      )}
    </div>
  );
}
